import type { LoginCredentials, RegisterData, AuthResponse, User } from '@/types/auth.types';
import { ApiClient, ApiError } from './client';

/**
 * Auth API - connects to backend /api/auth
 */
export const authAPI = {
  login: async (credentials: LoginCredentials): Promise<AuthResponse> => {
    const body = {
      email: credentials.email,
      password: credentials.password,
    };
    const response = await ApiClient.post<AuthResponse>('/auth/login', body);
    return response;
  },

  register: async (data: RegisterData): Promise<AuthResponse> => {
    const body = {
      name: data.name,
      email: data.email,
      password: data.password,
    };
    const response = await ApiClient.post<AuthResponse>('/auth/register', body);
    return response;
  },

  getMe: async (): Promise<User> => {
    const response = await ApiClient.get<{ user: User }>('/auth/me');
    return response.user;
  },

  changePassword: async (data: {
    currentPassword: string;
    newPassword: string;
  }): Promise<{ success: boolean }> => {
    return ApiClient.post<{ success: boolean }>('/auth/change-password', data);
  },

  changeEmail: async (data: { newEmail: string }): Promise<AuthResponse> => {
    return ApiClient.post<AuthResponse>('/auth/change-email', data);
  },

  // Solicita el email de restablecimiento. El backend responde siempre igual,
  // exista o no el email (no revela si la cuenta existe).
  forgotPassword: async (email: string): Promise<{ success: boolean }> => {
    return ApiClient.post<{ success: boolean }>('/auth/forgot-password', { email });
  },

  // Fija la nueva contraseña con el token recibido por email.
  resetPassword: async (data: {
    token: string;
    newPassword: string;
  }): Promise<{ success: boolean }> => {
    return ApiClient.post<{ success: boolean }>('/auth/reset-password', data);
  },

  // Valida el token de invitación (magic link) para la página "Activa tu cuenta".
  validateInvitation: async (
    token: string
  ): Promise<{ valid: boolean; email?: string; companyName?: string | null }> => {
    return ApiClient.get(`/invitations/validate/${encodeURIComponent(token)}`);
  },

  // Acepta la invitación: crea la cuenta y devuelve la sesión.
  acceptInvitation: async (data: {
    token: string;
    name: string;
    password: string;
  }): Promise<AuthResponse> => {
    return ApiClient.post<AuthResponse>('/auth/accept-invitation', data);
  },
};

export { ApiError };
